import { type TCoreButtonViewProps } from '@core/widgets';
import { useBehavior, useButton, useTriggerAction } from '@libs/core';
import { TButtonData, TWidgetCommonOption, TWidget } from '@libs/types';
import React, { ElementType, MouseEventHandler } from 'react';
import { TButtonRowEmphasised } from '../../types/data';
import { TRowOption } from '../../types/options';
import { RowEmphasisedView } from './RowEmphasisedView';

type TButtonRowEmphasisedWidget<TD, TO> = (
    | {
          isWidget: true;
          widget: TWidget<TD, TO>;
      }
    | ({
          isWidget: false;
          handleClick: MouseEventHandler;
          title?: string;
          expired?: boolean;
          subtitle?: string;
          meta?: string;
          iconName?: string;
          metaStyle?: TRowOption['metaVariant'];
          metaHierarchy?: TRowOption['metaHierarchy'];
      } & TWidgetCommonOption)
) & {
    as?: ElementType;
};

export const RowEmphasised = (props: TButtonRowEmphasisedWidget<TButtonRowEmphasised, TRowOption>) => {
    const { isWidget, as } = props;
    const finalWidget = isWidget
        ? props.widget
        : ({
              uid: '',
              data: {
                  iconName: props.iconName,
                  expired: props.expired,
                  messages: {
                      title: props.title,
                      subtitle: props.subtitle,
                      meta: props.meta,
                  },
              },
              options: {
                  metaVariant: props.metaStyle,
                  isDisabled: props.isDisabled,
              },
          } as TWidget<TButtonRowEmphasised, TRowOption>);

    const { isDisabled, isHidden } = useBehavior(finalWidget as TWidget<TButtonData>);
    const { triggerActionsByType, triggerActionsByNameAndType } = useTriggerAction(finalWidget);
    const { handleClick } = useButton(finalWidget, { isDisabled, triggerActionsByType, triggerActionsByNameAndType });
    const expiredOrDisabled = finalWidget.data?.expired ?? isDisabled ?? false;
    const parentStyleNames = [finalWidget.options?.style].filter(Boolean) as string[];

    return (
        <RowEmphasisedView
            widget={finalWidget as TCoreButtonViewProps['widget']}
            uid={finalWidget.uid}
            as={as}
            title={finalWidget.data?.messages?.title}
            subtitle={finalWidget.data?.messages?.subtitle}
            meta={finalWidget.data?.messages?.meta}
            iconName={finalWidget.data?.iconName}
            avatar={finalWidget.data?.avatar}
            metaVariant={finalWidget.options?.metaVariant}
            isDisabled={isDisabled}
            isContentDisabled={expiredOrDisabled}
            isHide={isHidden}
            margin={finalWidget.options?.margin}
            parentStyleNames={parentStyleNames}
            onClick={handleClick}
        />
    );
};
